import { LockOutlined, UserOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Input } from "antd"
import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import Icon from "../../Icon"
import { IS_PROD } from "../../constants/dev"
import { normalizeEmail } from "../../utils/form/normalizeEmail"
import SocialProviders from "./SocialProviders/SocialProviders"

type FormErrorText = string | string[]

interface LoginFormValues {
  email: string
  password: string
  otp_pass?: string | null
}

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => void
  error?: string | Record<string, FormErrorText> | null
  otpRequired?: boolean
  socialAuth: (data: Record<string, unknown>) => void
  resetError: () => void
  showForgotPassword?: boolean
  providers?: string[]
  loading?: boolean
  children?: React.ReactNode
}

const alertIcon = "alert_triangle_error" as React.ComponentProps<
  typeof Icon
>["type"]

function LoginForm({
  onSubmit,
  error,
  otpRequired,
  socialAuth,
  resetError,
  showForgotPassword = true,
  // providers = ["google", "apple"],
  providers = ["google"],
  loading = false,
  children = null
}: LoginFormProps) {
  const [errorText, setErrorText] = useState<FormErrorText | null>(null)
  const [isBackError, setIsBackError] = useState(false)

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (error) {
      setIsBackError(true)
      if (typeof error === "string") {
        setErrorText(error)
      } else {
        setErrorText(Object.values(error)[0])
      }
    } else {
      if (isBackError) setIsBackError(false)
      setErrorText(null)
    }
  }, [error])

  const onFinish = (values: LoginFormValues) => {
    onSubmit(values)
  }

  const onFinishFailed = ({
    errorFields
  }: {
    errorFields?: Array<{ errors: string[] }>
  }) => {
    if (errorFields) {
      setErrorText(errorFields[0]["errors"])
    }
  }

  function handleClearError(
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
    setIsBackError(false)
    resetError()
  }

  function onBlurHandler(e: React.FocusEvent<HTMLInputElement>) {
    const target = e.target

    setTimeout(() => {
      const error = form.getFieldError(target.name)
      if (error.length) setErrorText(error[0])
    }, 10)
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger={["onSubmit", "onChange"]}
      className="login-form"
      layout="vertical"
      form={form}
      disabled={loading}
      initialValues={
        IS_PROD
          ? {}
          : {
              email: import.meta.env.VITE_APP_DEV_ACCOUNT_LOGIN,
              password: import.meta.env.VITE_APP_DEV_ACCOUNT_PASSWORD
            }
      }
    >
      {children}

      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div
            className={clsx("error-text", { "error-text_empty": !errorText })}
          >
            {errorText && <Icon type={alertIcon} role={"icon"} />}
            <span>{errorText}</span>
          </div>
        )}
      </Form.Item>

      <Form.Item
        // label="Email"
        name="email"
        validateStatus={errorText || isBackError ? "error" : undefined}
        normalize={normalizeEmail}
        rules={[
          {
            type: "email",
            message: t("The email or password you entered is incorrect.")
          },
          {
            required: true,
            message: t("Email and password fields cannot be empty.")
          }
        ]}
      >
        <Input
          size="large"
          placeholder={t("Email address")}
          name="email"
          type="email"
          prefix={<UserOutlined className={"text-gray-500"} />}
          onChange={(e) => handleClearError(e, "email")}
          onBlur={onBlurHandler}
          autoComplete="username"
        />
      </Form.Item>

      <Form.Item
        // label="Password"
        name="password"
        validateStatus={errorText || isBackError ? "error" : undefined}
        rules={[
          {
            required: true,
            message: t("Email and password fields cannot be empty.")
          }
        ]}
      >
        <Input.Password
          size="large"
          placeholder={t("Password")}
          autoComplete="new-password"
          prefix={<LockOutlined className={"text-gray-500"} />}
          visibilityToggle
          onChange={(e) => handleClearError(e, "password")}
        />
      </Form.Item>

      {otpRequired ? (
        <Form.Item
          label="OTP"
          name="otp_pass"
          initialValue={""}
          validateStatus={errorText || isBackError ? "error" : undefined}
          rules={[{ required: true, message: t("OTP is required") }]}
        >
          <Input
            size="large"
            placeholder={t("6-digit Code")}
            name="otp_pass"
            type="number"
            onChange={(e) => handleClearError(e, "otp")}
          />
        </Form.Item>
      ) : null}

      {showForgotPassword && (
        <Flex className={"mb-3"} justify="flex-end">
          <Link to="/restore-password" onClick={resetError}>
            {t("Forgot your password?")}
          </Link>
        </Flex>
      )}

      <Flex gap={"middle"} vertical>
        <Button
          className={"!mt-0"}
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={loading}
        >
          {t("Sign In")}
        </Button>

        <SocialProviders
          providers={providers}
          socialAuth={socialAuth}
          loading={loading}
        />
      </Flex>
    </Form>
  )
}

export default LoginForm
